import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bug, FileClock, FileWarning, Gauge, Info, Loader2, Radio, ShieldCheck, Trash2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { ZuzycieDanych } from '@/pages/ZuzycieDanych';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import {
  fetchAudit,
  fetchDiagnostic,
  fetchLogStats,
  LOG_LEVELS,
  type AuditRow,
  type DiagnosticRow,
  type LogLevel,
} from '@/lib/logs-api';
import { cn } from '@/lib/utils';
import { LogiSps } from '@/pages/LogiSps';
import { LiveSmartSync } from '@/components/LiveSmartSync';
import { ostrzegaj, rodzajAktora } from '@/lib/aktor-audytu';

/**
 * Logi portalu — dwa dzienniki o RÓŻNYCH gwarancjach, świadomie rozdzielone także w UI.
 *
 * Log diagnostyczny wolno stracić i jest rotowany; ślad audytu nie jest rotowany nigdy.
 * Wspólna tabela z kolumną „rodzaj" sugerowałaby, że wolno je czytać jednakowo — a wtedy
 * pytanie „dlaczego nie ma wpisu z marca" ma dwie zupełnie różne odpowiedzi.
 *
 * Nagłówek każdej zakładki mówi wprost, co się z jej wpisami stanie: przy diagnostyce widać
 * limity i zapełnienie, przy audycie stoi „bez rotacji". Bez tego pierwsze pytanie po awarii
 * („czy to się w ogóle zapisało?") wymaga zaglądania do konfiguracji serwera.
 */

const LEVEL_ICON: Record<LogLevel, typeof Info> = {
  DEBUG: Bug,
  INFO: Info,
  WARN: AlertTriangle,
  ERROR: XCircle,
};

const LEVEL_CLASS: Record<LogLevel, string> = {
  DEBUG: 'text-muted-foreground',
  INFO: 'text-sky-600 dark:text-sky-400',
  WARN: 'text-amber-600 dark:text-amber-400',
  ERROR: 'text-destructive',
};

function when(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'medium' });
}

/** Szczegóły wpisu — surowy JSON, bo to dane techniczne i każda „ładna" forma coś gubi. */
function Detail({ value }: { value: unknown }) {
  if (value === null || value === undefined) return null;
  return (
    <pre className="mt-1 max-w-full overflow-x-auto rounded bg-muted/60 p-2 text-[11px] leading-relaxed">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function Logs() {
  const { t } = useI18n();
  const [tab, setTab] = useState<'live' | 'diagnostic' | 'audit' | 'traffic' | 'spsLog'>('live');

  const { data: stats } = useQuery({ queryKey: ['logs', 'stats'], queryFn: fetchLogStats });

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">{t('logs.title')}</h1>
      </header>

      <div className="flex flex-wrap gap-2">
        {/*
          ⚠️⚠️ „LIVE" STOI PIERWSZE I JEST DOMYŚLNE, bo odpowiada na pytanie, z którym się tu wchodzi
          najczęściej: „czy coś w ogóle leci ze Smart Synca". Zgłoszone 01.09.2026 z wdrożenia IKEA.
        */}
        <Button variant={tab === 'live' ? 'default' : 'outline'} size="sm" onClick={() => setTab('live')}>
          <Radio className="size-4" />
          {t('logs.tabLive')}
        </Button>
        <Button variant={tab === 'diagnostic' ? 'default' : 'outline'} size="sm" onClick={() => setTab('diagnostic')}>
          <FileClock className="size-4" />
          {t('logs.tabDiagnostic')}
          {stats ? <Badge variant="neutral">{stats.diagnostic.total}</Badge> : null}
        </Button>
        <Button variant={tab === 'audit' ? 'default' : 'outline'} size="sm" onClick={() => setTab('audit')}>
          <ShieldCheck className="size-4" />
          {t('logs.tabAudit')}
          {stats ? <Badge variant="neutral">{stats.audit.total}</Badge> : null}
        </Button>
        {/*
          ⚠️ TRZECIA ZAKŁADKA NA TYM SAMYM EKRANIE, a nie nowa pozycja w nawigacji: zużycie danych
          jest statystyką pracy portalu i agentów, czyli tym samym rodzajem pytania co logi —
          „co się tu dzieje i ile tego jest". Uprawnienie jest to samo (`service`).
        */}
        <Button variant={tab === 'traffic' ? 'default' : 'outline'} size="sm" onClick={() => setTab('traffic')}>
          <Gauge className="size-4" />
          {t('logs.tabTraffic')}
        </Button>
        {/*
          ⚠️ CZWARTA ZAKŁADKA: logi SAMEGO SUPPLYPOINTA, nie nasze. To jedyne miejsce w portalu,
          które widzi awarię programu na maszynie — agent pilnuje BAZY, a gdy SPS się wywala, baza
          jest zdrowa i portal nie ma o czym mówić. Uprawnienie to samo (`service`), bo pytanie jest
          tego samego rodzaju: „co się tam dzieje".
        */}
        <Button variant={tab === 'spsLog' ? 'default' : 'outline'} size="sm" onClick={() => setTab('spsLog')}>
          <FileWarning className="size-4" />
          {t('logs.tabSpsLog')}
        </Button>
      </div>

      {tab === 'live' ? (
        <LiveSmartSync />
      ) : tab === 'diagnostic' ? (
        <DiagnosticTab />
      ) : tab === 'audit' ? (
        <AuditTab />
      ) : tab === 'traffic' ? (
        <ZuzycieDanych />
      ) : (
        <LogiSps />
      )}
    </div>
  );
}

function DiagnosticTab() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const [minLevel, setMinLevel] = useState<LogLevel | ''>('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  /** filtr wyspy jest OPCJONALNY — wpisy o całej instancji nie mają wyspy i muszą być widoczne */
  const [onlyIsland, setOnlyIsland] = useState(false);

  const query = {
    groupId: onlyIsland ? (groupId ?? undefined) : undefined,
    minLevel: minLevel || undefined,
    source: source || undefined,
    search: search || undefined,
    page,
    pageSize: 50,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['logs', 'diagnostic', query],
    queryFn: () => fetchDiagnostic(query),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1 font-medium text-foreground">
            <Trash2 className="size-3.5" />
            {t('logs.rotated')}
          </span>
          {data ? (
            <>
              <span>
                {t('logs.limitDays')}: {data.limits.maxDays || '—'}
              </span>
              <span>
                {t('logs.limitRows')}: {data.limits.maxRows.toLocaleString('pl-PL')}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('logs.minLevel')}</span>
          <Select value={minLevel} onChange={(e) => { setMinLevel(e.target.value as LogLevel | ''); setPage(1); }}>
            <option value="">{t('logs.anyLevel')}</option>
            {LOG_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('logs.source')}</span>
          <Input value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} placeholder="api / agent:…" />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('logs.search')}</span>
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </label>
        <label className="flex items-center gap-2 pb-2 text-xs">
          <input type="checkbox" checked={onlyIsland} onChange={(e) => { setOnlyIsland(e.target.checked); setPage(1); }} />
          <span>{t('logs.onlyIsland')}</span>
        </label>
        {isFetching ? <Loader2 className="mb-2 size-4 animate-spin text-muted-foreground" /> : null}
      </div>

      <Rows
        total={data?.total ?? 0}
        page={page}
        setPage={setPage}
        empty={t('logs.emptyDiagnostic')}
        rows={data?.rows ?? []}
        render={(r: DiagnosticRow) => {
          const Icon = LEVEL_ICON[r.level];
          return (
            <li key={r.seq} className="border-b border-border/60 px-3 py-2 text-sm last:border-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className={cn('inline-flex items-center gap-1 font-medium', LEVEL_CLASS[r.level])}>
                  <Icon className="size-3.5" />
                  {r.level}
                </span>
                <span className="font-mono text-xs text-muted-foreground">{when(r.at)}</span>
                <Badge variant="neutral">{r.source}</Badge>
                {r.groupId === null ? <Badge variant="neutral">{t('logs.instanceWide')}</Badge> : null}
              </div>
              <p className="mt-1 break-words">{r.message}</p>
              <Detail value={r.context} />
            </li>
          );
        }}
      />
    </div>
  );
}

function AuditTab() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const [action, setAction] = useState('');
  const [outcome, setOutcome] = useState<'' | 'OK' | 'DENIED'>('');
  const [actor, setActor] = useState('');
  const [page, setPage] = useState(1);
  const [onlyIsland, setOnlyIsland] = useState(false);

  const query = {
    groupId: onlyIsland ? (groupId ?? undefined) : undefined,
    action: action || undefined,
    outcome: outcome || undefined,
    actor: actor || undefined,
    page,
    pageSize: 50,
  };

  const { data, isFetching } = useQuery({ queryKey: ['logs', 'audit', query], queryFn: () => fetchAudit(query) });

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1 font-medium text-foreground">
          <ShieldCheck className="size-3.5" />
          {t('logs.notRotated')}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('logs.action')}</span>
          <Input value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} placeholder="account. / auth. / config." />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('logs.outcome')}</span>
          <Select value={outcome} onChange={(e) => { setOutcome(e.target.value as '' | 'OK' | 'DENIED'); setPage(1); }}>
            <option value="">{t('logs.anyOutcome')}</option>
            <option value="OK">OK</option>
            <option value="DENIED">{t('logs.denied')}</option>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">{t('logs.actor')}</span>
          <Input value={actor} onChange={(e) => { setActor(e.target.value); setPage(1); }} />
        </label>
        <label className="flex items-center gap-2 pb-2 text-xs">
          <input type="checkbox" checked={onlyIsland} onChange={(e) => { setOnlyIsland(e.target.checked); setPage(1); }} />
          <span>{t('logs.onlyIsland')}</span>
        </label>
        {isFetching ? <Loader2 className="mb-2 size-4 animate-spin text-muted-foreground" /> : null}
      </div>

      <Rows
        total={data?.total ?? 0}
        page={page}
        setPage={setPage}
        empty={t('logs.emptyAudit')}
        rows={data?.rows ?? []}
        render={(r: AuditRow) => (
          <li key={r.seq} className="border-b border-border/60 px-3 py-2 text-sm last:border-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Badge variant={r.outcome === 'DENIED' ? 'destructive' : 'success'}>
                {r.outcome === 'DENIED' ? t('logs.denied') : 'OK'}
              </Badge>
              <span className="font-mono text-xs">{r.action}</span>
              <span className="font-mono text-xs text-muted-foreground">{when(r.at)}</span>
            </div>
            <p className="mt-1">
              {/* ODPIS tożsamości, nie żywe konto — dlatego pokazujemy e-mail zapisany we wpisie */}
              <span className="font-medium">{r.actorName ?? r.actorEmail}</span>
              {r.actorName ? <span className="text-muted-foreground"> ({r.actorEmail})</span> : null}
              {/*
                ⚠️⚠️ `actorId === null` MA PIĘĆ ZNACZEŃ, nie dwa — i jedna plakietka na wszystkie nie
                mówiła nic o żadnym. Zgłoszone 01.09.2026: „każde info jest jako »bez powiązanego
                konta« i nie bardzo wiadomo o co chodzi". Rozpoznanie i uzasadnienie: `aktor-audytu.ts`.

                ⚠️ OSTRZEGA WYŁĄCZNIE BRAK KONTA PRZY PRAWDZIWYM E-MAILU. Portal, agent i integracja
                to normalne, spodziewane sprawcy — żółta plakietka przy każdym z nich zamieniała
                ostrzeżenie w tło.
              */}
              {(() => {
                const rodzaj = rodzajAktora(r.actorId, r.actorEmail);
                if (rodzaj === 'KONTO') return null;
                return (
                  <Badge variant={ostrzegaj(rodzaj) ? 'warning' : 'neutral'} className="ml-2">
                    {t(`logs.aktor.${rodzaj}`)}
                  </Badge>
                );
              })()}
              <span className="text-muted-foreground">
                {' · '}
                {r.subjectType}
                {r.subjectId ? `: ${r.subjectId}` : ''}
              </span>
            </p>
            <Detail value={r.detail} />
          </li>
        )}
      />
    </div>
  );
}

/** Lista + stronicowanie, wspólne dla obu zakładek — różni je zawartość wiersza, nie mechanika. */
function Rows<T>({
  rows,
  render,
  total,
  page,
  setPage,
  empty,
}: {
  rows: T[];
  render: (row: T) => React.ReactNode;
  total: number;
  page: number;
  setPage: (p: number) => void;
  empty: string;
}) {
  const { t } = useI18n();
  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="rounded-md border border-border">
      {rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul>{rows.map(render)}</ul>
      )}
      {total > 50 ? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs">
          <span className="text-muted-foreground">
            {t('logs.count')}: {total}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              ←
            </Button>
            <span>
              {page} / {pages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              →
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
